import { createQueryKeys } from '@lukemorales/query-key-factory'
import apiClient from '../service/api'
import { PaginationParams } from '../types/list'
import {
  ALL_SCHEDULE_STATUSES,
  CreditPoolView,
  TransactionScheduleStatus,
  WorkspaceActivityView,
} from '../types/credit'
import { BillingHistoryItem, Invoice } from '../types/refund'

// GET Workspaces
export const getWorkspaces = async (page: number) => {
  const { data } = await apiClient.get('/v1/workspaces', {
    params: { page },
  })
  return data
}

export const getWorkspaceDetails = async (workspaceId: number) => {
  const { data } = await apiClient.get(`/v1/workspaces/${workspaceId}`)
  return data
}

export const getAvailableCredit = async (workspaceId: number) => {
  const { data } = await apiClient.get(`/v1/workspaces/${workspaceId}/credits`)
  return data
}

/** 어드민 부여. 양수만 받는다 — 회수는 `recallCredits` 로 분리됐다. 에러를 삼키면 실패가 화면에 안 뜬다 */
export const rechargeCredits = async (requestBody: { workspaceId: number; amount: number; memo: string }) => {
  const { data } = await apiClient.post(`/v1/workspaces/${requestBody.workspaceId}/credits`, requestBody, {
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return data
}

/**
 * 출처별 크레딧 잔량. 체크포인트가 없어도 현행 윈도우 재생값을 `basis: 'FALLBACK'` 으로 내려준다
 * (aivatar #413) — 어드민 회수 상한이 쓰는 값과 같아야 화면과 회수 동작의 세계관이 맞는다.
 *
 * 없는 워크스페이스는 404 → `null`. #413 배포 전 응답인 204 도 같이 `null` 로 받는다.
 */
export const getCreditPools = async (workspaceId: number): Promise<CreditPoolView | null> => {
  try {
    const { status, data } = await apiClient.get(`/v1/admin/workspaces/${workspaceId}/credit-pools`)
    if (status === 204 || !data) return null
    return data
  } catch (e: any) {
    if (e?.response?.status === 404) return null
    throw e
  }
}

export const getWorkspaceInvoices = async (workspaceId: number, params?: PaginationParams) => {
  const { data } = await apiClient.get(`/v1/workspaces/${workspaceId}/invoices`, { params })
  return data as { content: Invoice[]; totalElements: number }
}

/**
 * 청구 내역 — 구독 결제와 크레딧 팩 구매를 결제일 순 한 목록으로 (aivatar #533 · D-125).
 *
 * 기본 결제일 최신순, `sort=paidAt,asc` 면 오름차순(다른 정렬 칸은 서버가 무시한다).
 * 행의 id 는 종류에 따라 `invoiceId` / `paymentOrderId` 로 나뉜다 — `refundTargetOf` 로 읽는다.
 */
export const getWorkspaceBillingHistory = async (workspaceId: number, params?: PaginationParams) => {
  const { data } = await apiClient.get(`/v1/workspaces/${workspaceId}/billing-history`, { params })
  return data as { content: BillingHistoryItem[]; totalElements: number }
}

/**
 * 비활성 파생 상태. 월 경계가 Java `LocalDate.plusMonths` 의 말일 보정을 따르고 반열림이라
 * 프론트에서 dayjs 로 재현하지 않는다(그 하루가 데이터 보존 단계 판정이다).
 * 없는 워크스페이스는 404 → `null`.
 */
export const getWorkspaceActivityState = async (
  workspaceId: number,
): Promise<WorkspaceActivityView | null> => {
  try {
    const { data } = await apiClient.get(`/v1/admin/workspaces/${workspaceId}/activity-state`)
    return data
  } catch (e: any) {
    if (e?.response?.status === 404) return null
    throw e
  }
}

export type RecallCreditsResponse = {
  requested: number
  /** 실제 회수액. 요청량이 어드민 부여 풀 잔량을 넘으면 잔량까지만 회수된다 */
  recalled: number
  /** 부족분 = 요청 - 실제. 0 이면 전액 회수 */
  shortfall: number
  adminGrantRemaining: number
}

/**
 * 어드민 부여분 회수. 충전 API 에 음수를 넣던 방식을 대체한다.
 *
 * amount 는 **양수** 로 보낸다. 서버가 어드민 부여 풀 총잔량을 상한으로 잡아
 * MIN(요청, 잔량) 만큼만 회수하며, 구독·쿠폰·단건 구매분은 침범하지 않는다.
 * 잔량을 넘긴 요청은 실패가 아니라 부분 회수로 처리되고 shortfall 에 부족분이 담긴다.
 */
export const recallCredits = async (requestBody: { workspaceId: number; amount: number; memo: string }) => {
  const { data } = await apiClient.post<RecallCreditsResponse>(
    `/v1/admin/workspaces/${requestBody.workspaceId}/credit-pools/recall`,
    { amount: requestBody.amount, memo: requestBody.memo },
  )
  return data
}

export const getSubscriptions = async (page: number, workspaceId: number) => {
  const { data } = await apiClient.get(`/v1/admin/workspaces/${workspaceId}/subscriptions`, {
    params: { page },
  })
  return data
}

type GetScheduleCreditsListParams = PaginationParams & {
  min?: number
  max?: number
  /** 미지정이면 4개 상태 전부. 백엔드가 필수 파라미터로 받는다(콤마 구분) */
  status?: TransactionScheduleStatus[]
}

export const getScheduleCreditsList = async (workspaceId: number, params: GetScheduleCreditsListParams) => {
  const { status, ...rest } = params
  const { data } = await apiClient.get(`/v1/workspaces/${workspaceId}/scheduleCredits`, {
    params: { ...rest, status: (status ?? ALL_SCHEDULE_STATUSES).join(',') },
  })
  return data
}

export const scheduleCredits = async (requestBody: {
  workspaceId: number
  amount: number
  memo: string
  scheduledAt: string
}) => {
  const { data } = await apiClient.post(`/v1/workspaces/${requestBody.workspaceId}/scheduleCredits`, requestBody)
  return data
}

export const unscheduleCredits = async (scheduleId: number) => {
  const { data } = await apiClient.post(`/v1/workspaces/unscheduleCredits/${scheduleId}`)
  return data
}

export const schedulePlans = async (requestBody: {
  workspaceId: number
  planId: number
  assignTime: string
  revokeTime: string
}) => {
  const { data } = await apiClient.post(`/v1/workspaces/${requestBody.workspaceId}/schedulePlans`, requestBody)
  return data
}

export const unschedulePlans = async (scheduleId: number) => {
  const { data } = await apiClient.post(`/v1/workspaces/unSchedulePlans/${scheduleId}`)
  return data
}

export const changePlanRevocationDate = async ({
                                                 scheduleId,
                                                 ...requestBody
                                               }: {
  scheduleId: number
  revokeTime: string
}) => {
  const { data } = await apiClient.patch(`/v1/workspaces/schedulePlans/${scheduleId}/revocationDate`, requestBody)
  return data
}

export const getSchedulePlans = async (workspaceId: number, params?: PaginationParams) => {
  const { data } = await apiClient.get(`/v1/workspaces/${workspaceId}/schedulePlans`, { params })
  return data
}

export const getWorkspaceReceipts = async (workspaceId: number) => {
  const { data } = await apiClient.get(`/v1/workspaces/${workspaceId}/receipts`)
  return data
}

export const workspaceQueries = createQueryKeys('workspace', {
  detail: (workspaceId: Parameters<typeof getWorkspaceDetails>[0]) => ({
    queryKey: [workspaceId],
    queryFn: () => getWorkspaceDetails(workspaceId),
    contextQueries: {
      credits: {
        queryKey: null,
        queryFn: () => getAvailableCredit(workspaceId),
      },
      scheduleCredits: (params: GetScheduleCreditsListParams) => ({
        queryKey: [params],
        queryFn: () => getScheduleCreditsList(workspaceId, params),
      }),
      schedulePlans: (params?: PaginationParams) => ({
        queryKey: [params],
        queryFn: () => getSchedulePlans(workspaceId, params),
      }),
      receipts: {
        queryKey: null,
        queryFn: () => getWorkspaceReceipts(workspaceId),
      },
      creditPools: {
        queryKey: null,
        queryFn: () => getCreditPools(workspaceId),
      },
      invoices: (params?: PaginationParams) => ({
        queryKey: [params],
        queryFn: () => getWorkspaceInvoices(workspaceId, params),
      }),
      billingHistory: (params?: PaginationParams) => ({
        queryKey: [params],
        queryFn: () => getWorkspaceBillingHistory(workspaceId, params),
      }),
      activityState: {
        queryKey: null,
        queryFn: () => getWorkspaceActivityState(workspaceId),
      },
    },
  }),
})
